import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, MessageSquare, Heart, MapPin, Briefcase, GraduationCap, Play, Crown, Award } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ProfileSetupData, Story } from '../types';
import { theme } from '../../../styles/theme';
import { getPersonaInfo } from '../../../utils/personaUtils';
import { StoryUploadModal, StoryViewer } from '../Stories';
import { ProfileSafetyActions } from '../../shared/ProfileSafetyActions';
import { useAuthContext } from '../../../providers';
import { swipeService } from '../../../services/Swipe/swipeService';
import { momentsService } from '../../../services/SocialFeed/momentsService';
import { Icon } from '../../../components/shared';
import {
  ProfileViewContainer,
  ProfileViewContent,
  ProfileCard,
  ProfileHeader,
  ProfilePhoto,
  ProfilePhotoGrid,
  PhotoItem,
  ProfileInfo,
  ProfileName,
  ProfileAge,
  ProfileLocation,
  ProfileBio,
  ProfileInterests,
  InterestTag,
  ProfilePreferences,
  PreferenceItem,
  ProfileStats,
  StatItem,
  StatValue,
  StatLabel,
  ProfileCompletion,
  CompletionBar,
  CompletionText,
  ProfileActions,
  MessageButton,
  EditProfileButton,
  ProfileActionsCompact,
  MessageButtonCompact,
  AddToDeckButtonCompact,
  ProfileNameContainer,
  ProfilePersona,
  PremiumBadge,
  IdentityContainer,
  ProfileBadgePill,
  StoryPlusButton,
  AddToDeckButton,
  PhotoViewerOverlay,
  PhotoViewerContent,
  PhotoViewerImage,
  PhotoViewerCloseButton,
  PhotoViewerNavButton,
  PhotoViewerIndicator,
  VideoIndicator
} from '../../../styles/Profile/ProfileView.styles';
import {
  ProfileNavigationContainer,
  ProfileNavTabs,
  ProfileNavTab,
  TabIcon,
  TabLabel,
  ProfileTabContent
} from '../../../styles/Profile/ProfileNavigation.styles';
import { logger } from '../../../utils/logger';

interface ProfileViewProps {
  profileData: ProfileSetupData;
  isOwnProfile?: boolean;
  /**
   * Show Report and Block at the end of the profile.
   *
   * On by default, because every surface showing someone else's profile needs
   * them. The peek drawer turns it off: it carries the same two actions in its
   * own footer, and one profile offering two Block buttons invites them to
   * disagree about what happened.
   */
  showSafetyActions?: boolean;
  targetUserId?: string; // The userId of the profile being viewed
  onEdit?: () => void;
  onShare?: () => void;
  onMessage?: () => void;
  onLike?: () => void;
}

export const ProfileView: React.FC<ProfileViewProps> = ({
  profileData,
  isOwnProfile = false,
  showSafetyActions = true,
  targetUserId,
  onEdit,
  onShare,
  onMessage,
  onLike
}) => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState<'photos' | 'about' | 'interests'>('photos');

  // Story state
  const [stories, setStories] = useState<Story[]>([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isStoryViewerOpen, setIsStoryViewerOpen] = useState(false);

  // Photo Viewer State
  const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  // Deck state
  const [isInDeck, setIsInDeck] = useState(false);
  const [isTogglingDeck, setIsTogglingDeck] = useState(false);

  // Load this profile's real Moments. Replaces a hardcoded BigBuckBunny sample
  // that was injected for every own-profile view, which made the plus icon look
  // like it had already posted something.
  useEffect(() => {
    const momentsOwnerId = targetUserId || user?.id;
    if (!momentsOwnerId) return;

    let cancelled = false;
    momentsService.getUserMoments(momentsOwnerId)
      .then((loaded) => { if (!cancelled) setStories(loaded); })
      .catch((error) => logger.error('[MOMENTS] Failed to load profile moments:', error));

    return () => { cancelled = true; };
  }, [targetUserId, user?.id]);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= parseInt(theme.breakpoints.mobile));
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const calculateAge = (dateOfBirth: Date): number => {
    const today = new Date();
    let age = today.getFullYear() - dateOfBirth.getFullYear();
    const monthDiff = today.getMonth() - dateOfBirth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dateOfBirth.getDate())) {
      age--;
    }

    return age;
  };

  const getProfileCompletion = () => {
    const totalPhotos = profileData.photos.length;
    const hasMainPhoto = profileData.photos.some(photo => photo.isMain);
    const hasBio = profileData.bio.content.length > 50;
    const hasInterests = profileData.interests.length >= 3;
    const hasLocation = profileData.basicInfo.location.city && profileData.basicInfo.location.state;
    const hasBasicInfo = profileData.basicInfo.firstName && profileData.basicInfo.lastName;

    const completedItems = [
      hasMainPhoto,
      hasBio,
      hasInterests,
      hasLocation,
      hasBasicInfo,
      totalPhotos >= 3
    ].filter(Boolean).length;

    return Math.round((completedItems / 6) * 100);
  };

  const getProfileStats = () => {
    const totalPhotos = profileData.photos.length;
    const validatedPhotos = profileData.photos.filter(photo => photo.isValidated).length;
    const totalInterests = profileData.interests.length;
    const bioLength = profileData.bio.content.length;
    const completionPercentage = getProfileCompletion();

    return {
      totalPhotos,
      validatedPhotos,
      totalInterests,
      bioLength,
      completionPercentage
    };
  };

  const stats = getProfileStats();

  // Use the main photo for the profile picture, or fallback to the first one
  // An image, never the video. Both can be main now, so a type-blind
  // find() would put a video file where a photo belongs.
  const profileImages = profileData.photos.filter(photo => photo.type !== 'video');
  const mainPhoto = profileImages.find(photo => photo.isMain) || profileImages[0];
  const age = calculateAge(profileData.basicInfo.dateOfBirth);

  // Filter active stories (not expired)
  const activeStories = stories.filter(story => {
    const now = new Date();
    return story.expiresAt > now && story.isActive;
  });

  const hasActiveStories = activeStories.length > 0;

  // Debug logging (remove in production)
  useEffect(() => {
    logger.log('Story Debug:', {
      totalStories: stories.length,
      activeStories: activeStories.length,
      hasActiveStories,
      isOwnProfile,
      userId: user?.id
    });
  }, [stories, activeStories, hasActiveStories, isOwnProfile, user]);

  // Check for expired stories and update state
  useEffect(() => {
    const interval = setInterval(() => {
      setStories(prev => prev.filter(story => {
        const now = new Date();
        return story.expiresAt > now;
      }));
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  // Check if user is in deck when viewing other profiles
  useEffect(() => {
    const checkDeckStatus = async () => {
      if (!isOwnProfile && user?.id && targetUserId) {
        try {
          const inDeck = await swipeService.isInDeck(user.id, targetUserId);
          setIsInDeck(inDeck);
        } catch (error) {
          console.error('Failed to check deck status:', error);
        }
      }
    };

    checkDeckStatus();
  }, [isOwnProfile, user?.id, targetUserId]);

  // The modal has already uploaded the video to Storage and registered the
  // Moment by this point, so the saved record just joins local state. It used
  // to substitute a random Google sample clip here, which meant a posted Moment
  // never contained what the user actually recorded.
  const handleStoryUpload = (story: Story) => {
    setStories(prev => [story, ...prev]);
  };

  const handleProfilePhotoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (hasActiveStories) {
      setIsStoryViewerOpen(true);
    } else {
      // Open full screen photo viewer starting at current active photo
      // Find index of the displayed main photo
      const index = profileData.photos.findIndex(p => p.id === mainPhoto?.id);
      setViewerIndex(index >= 0 ? index : 0);
      setIsPhotoViewerOpen(true);
    }
  };

  const handleNextPhoto = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setViewerIndex((prev) => (prev + 1) % profileData.photos.length);
  };

  const handlePrevPhoto = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setViewerIndex((prev) => (prev - 1 + profileData.photos.length) % profileData.photos.length);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isPhotoViewerOpen) return;

      if (e.key === 'ArrowRight') handleNextPhoto();
      if (e.key === 'ArrowLeft') handlePrevPhoto();
      if (e.key === 'Escape') setIsPhotoViewerOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPhotoViewerOpen]);

  const handlePlusButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUploadModalOpen(true);
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    }
    // Edit profile functionality removed
  };

  const handleShare = () => {
    if (onShare) {
      onShare();
    } else {
      // Default share functionality
      if (navigator.share) {
        navigator.share({
          title: `${profileData.basicInfo.firstName}'s Profile`,
          text: `Check out ${profileData.basicInfo.firstName}'s profile on Plenty of Hearts!`,
          url: window.location.href
        });
      } else {
        // Fallback: copy to clipboard
        navigator.clipboard.writeText(window.location.href);
        alert('Profile link copied to clipboard!');
      }
    }
  };

  const handleMessage = () => {
    if (onMessage) {
      onMessage();
    } else {
      navigate('/messaging');
    }
  };

  const handleLike = () => {
    if (onLike) {
      onLike();
    } else {
      // Default like functionality
      logger.log('Liked profile');
    }
  };

  const handleToggleDeck = async () => {
    if (!user?.id || !targetUserId || isOwnProfile) return;

    setIsTogglingDeck(true);
    try {
      if (isInDeck) {
        await swipeService.removeFromDeck(user.id, targetUserId);
        setIsInDeck(false);
      } else {
        await swipeService.addToDeck(user.id, targetUserId);
        setIsInDeck(true);
      }
    } catch (error) {
      console.error('Failed to toggle deck:', error);
    } finally {
      setIsTogglingDeck(false);
    }
  };

  return (
    <>
      <ProfileViewContainer>
        <ProfileViewContent>
          {/* Profile Card */}
          <ProfileCard>
            <ProfileHeader>
              <ProfilePhoto
                $hasStories={hasActiveStories}
                onClick={handleProfilePhotoClick}
              >
                {mainPhoto ? (
                  <img
                    src={mainPhoto.type === 'video' && mainPhoto.thumbnail ? mainPhoto.thumbnail : mainPhoto.url}
                    alt={`${profileData.basicInfo.firstName}'s profile`}
                  />
                ) : (
                  <div className="placeholder">📷</div>
                )}
                {isOwnProfile && (
                  <StoryPlusButton
                    onClick={handlePlusButtonClick}
                    title="Add Moment"
                    aria-label="Add Moment"
                  >
                    +
                  </StoryPlusButton>
                )}
              </ProfilePhoto>

              <ProfileNameContainer>
                <ProfileName>
                  {profileData.basicInfo.firstName} {profileData.basicInfo.lastName}
                </ProfileName>
                {profileData.isPremium && (
                  <PremiumBadge title="Premium Member">
                    <Crown size={24} fill="#FFD700" strokeWidth={1} />
                  </PremiumBadge>
                )}
              </ProfileNameContainer>



              {/* Action Buttons (only show if not own profile) - positioned under persona */}
              {!isOwnProfile && (
                <ProfileActionsCompact>
                  <AddToDeckButtonCompact
                    onClick={handleToggleDeck}
                    disabled={isTogglingDeck}
                    $isAdded={isInDeck}
                  >
                    {isTogglingDeck ? (
                      isInDeck ? "Removing..." : "Adding..."
                    ) : isInDeck ? (
                      <>✓ Added</>
                    ) : (
                      "Add to Deck"
                    )}
                  </AddToDeckButtonCompact>
                  <MessageButtonCompact onClick={handleMessage}>
                    💬 Message
                  </MessageButtonCompact>
                </ProfileActionsCompact>
              )}

              <ProfileInfo>
                <ProfileAge>{age} years old</ProfileAge>
                <ProfileLocation>
                  <Icon icon={MapPin} size={16} /> {profileData.basicInfo.location.city}, {profileData.basicInfo.location.state}
                </ProfileLocation>

                {profileData.basicInfo.occupation && (
                  <div className="occupation">
                    <Icon icon={Briefcase} size={16} /> {profileData.basicInfo.occupation}
                  </div>
                )}

                {profileData.basicInfo.education && (
                  <div className="education">
                    <Icon icon={GraduationCap} size={16} /> {profileData.basicInfo.education}
                  </div>
                )}

                {/* Bio Section */}
                {profileData.bio && profileData.bio.content && (
                  <div className="bio">
                    <p>{profileData.bio.content}</p>
                  </div>
                )}
              </ProfileInfo>

              <IdentityContainer>
                {/* Persona Display */}
                {profileData.primaryPersona && (() => {
                  const personaInfo = getPersonaInfo(profileData.primaryPersona);
                  return personaInfo ? (
                    <ProfilePersona>
                      {personaInfo.icon && <span className="persona-icon">{personaInfo.icon}</span>}
                      <span className="persona-name">{personaInfo.name}</span>
                    </ProfilePersona>
                  ) : null;
                })()}

                {/* Heart Choices Badge — and, on your own profile, the way
                    back to the results that earned it. Replaying a 10-question
                    game to re-read your own personality type is the kind of
                    thing people simply do not do, so the badge is the door. */}
                {profileData.badges?.includes('heart-choices') && (
                  <ProfileBadgePill
                    $type="heart-choices"
                    $clickable={isOwnProfile}
                    onClick={isOwnProfile ? () => navigate('/heart-games/profile') : undefined}
                    role={isOwnProfile ? 'button' : undefined}
                    tabIndex={isOwnProfile ? 0 : undefined}
                    onKeyDown={isOwnProfile
                      ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate('/heart-games/profile');
                        }
                      }
                      : undefined}
                    title={isOwnProfile ? 'View your Heart Profile' : undefined}
                  >
                    <Award size={16} />
                    <span>Heart Choices Badge</span>
                  </ProfileBadgePill>
                )}
              </IdentityContainer>
            </ProfileHeader>

            {/* TikTok-style Navigation */}
            <ProfileNavigationContainer>
              <ProfileNavTabs>
                <ProfileNavTab
                  $isActive={activeTab === 'photos'}
                  onClick={() => setActiveTab('photos')}
                >
                  <TabIcon>
                    <Icon icon={Camera} size={20} />
                  </TabIcon>
                  <TabLabel>Media</TabLabel>
                </ProfileNavTab>
                <ProfileNavTab
                  $isActive={activeTab === 'about'}
                  onClick={() => setActiveTab('about')}
                >
                  <TabIcon>
                    <Icon icon={MessageSquare} size={20} />
                  </TabIcon>
                  <TabLabel>About</TabLabel>
                </ProfileNavTab>
                <ProfileNavTab
                  $isActive={activeTab === 'interests'}
                  onClick={() => setActiveTab('interests')}
                >
                  <TabIcon>
                    <Icon icon={Heart} size={20} />
                  </TabIcon>
                  <TabLabel>Interests</TabLabel>
                </ProfileNavTab>
              </ProfileNavTabs>
            </ProfileNavigationContainer>

            {/* Photos Tab */}
            <ProfileTabContent $isVisible={activeTab === 'photos'}>
              {profileData.photos.length > 0 && (
                <ProfilePhotoGrid>
                  {profileData.photos.map((photo, index) => (
                    <PhotoItem
                      key={photo.id}
                      onClick={() => {
                        setViewerIndex(index);
                        setIsPhotoViewerOpen(true);
                      }}
                    >
                      <img
                        src={photo.type === 'video' && photo.thumbnail ? photo.thumbnail : photo.url}
                        alt={`Media ${index + 1}`}
                      />
                      {photo.type === 'video' && (
                        <VideoIndicator>
                          <Play size={12} fill="white" strokeWidth={0} />
                        </VideoIndicator>
                      )}
                    </PhotoItem>
                  ))}
                </ProfilePhotoGrid>
              )}
            </ProfileTabContent>

            {/* About Tab */}
            <ProfileTabContent $isVisible={activeTab === 'about'}>
              {/* Bio */}
              {profileData.bio.content && (
                <ProfileBio>
                  <h4>About Me</h4>
                  <p>{profileData.bio.content}</p>
                </ProfileBio>
              )}

              {/* Basic Information */}
              <ProfilePreferences>
                <h4>Basic Information</h4>
                <PreferenceItem>
                  <strong>Occupation:</strong> {profileData.basicInfo.occupation || 'Not specified'}
                </PreferenceItem>
                <PreferenceItem>
                  <strong>Education:</strong> {profileData.basicInfo.education || 'Not specified'}
                </PreferenceItem>
                <PreferenceItem>
                  <strong>Gender:</strong> {profileData.basicInfo.gender ? profileData.basicInfo.gender.charAt(0).toUpperCase() + profileData.basicInfo.gender.slice(1) : 'Not specified'}
                </PreferenceItem>
                <PreferenceItem>
                  <strong>Height:</strong> {profileData.basicInfo.height || 'Not specified'}
                </PreferenceItem>
                <PreferenceItem>
                  <strong>Body Type:</strong> {profileData.basicInfo.bodyType ? profileData.basicInfo.bodyType.charAt(0).toUpperCase() + profileData.basicInfo.bodyType.slice(1).replace(/-/g, ' ') : 'Not specified'}
                </PreferenceItem>
                <PreferenceItem>
                  <strong>Relationship Status:</strong> {profileData.basicInfo.relationshipStatus ? profileData.basicInfo.relationshipStatus.charAt(0).toUpperCase() + profileData.basicInfo.relationshipStatus.slice(1).replace(/-/g, ' ') : 'Not specified'}
                </PreferenceItem>
                <PreferenceItem>
                  <strong>Looking For:</strong> {profileData.basicInfo.lookingFor ? profileData.basicInfo.lookingFor.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Not specified'}
                </PreferenceItem>
              </ProfilePreferences>

              {/* Lifestyle */}
              <ProfilePreferences>
                <h4>Lifestyle</h4>
                <PreferenceItem>
                  <strong>Drinking:</strong> {profileData.basicInfo.drinking ? profileData.basicInfo.drinking.charAt(0).toUpperCase() + profileData.basicInfo.drinking.slice(1).replace(/-/g, ' ') : 'Not specified'}
                </PreferenceItem>
                <PreferenceItem>
                  <strong>Smoking:</strong> {profileData.basicInfo.smoking ? profileData.basicInfo.smoking.charAt(0).toUpperCase() + profileData.basicInfo.smoking.slice(1).replace(/-/g, ' ') : 'Not specified'}
                </PreferenceItem>
                <PreferenceItem>
                  <strong>Exercise:</strong> {profileData.basicInfo.exercise ? profileData.basicInfo.exercise.charAt(0).toUpperCase() + profileData.basicInfo.exercise.slice(1).replace(/-/g, ' ') : 'Not specified'}
                </PreferenceItem>
              </ProfilePreferences>

              {/* Background */}
              <ProfilePreferences>
                <h4>Background</h4>
                <PreferenceItem>
                  <strong>Religion:</strong> {profileData.basicInfo.religion ? profileData.basicInfo.religion.charAt(0).toUpperCase() + profileData.basicInfo.religion.slice(1).replace(/-/g, ' ') : 'Not specified'}
                </PreferenceItem>
                <PreferenceItem>
                  <strong>Political Views:</strong> {profileData.basicInfo.politicalViews ? profileData.basicInfo.politicalViews.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Not specified'}
                </PreferenceItem>
                <PreferenceItem>
                  <strong>Languages:</strong> {profileData.basicInfo.languages && profileData.basicInfo.languages.length > 0 ? profileData.basicInfo.languages.join(', ') : 'Not specified'}
                </PreferenceItem>
              </ProfilePreferences>

              {/* Family & Pets */}
              <ProfilePreferences>
                <h4>Family & Pets</h4>
                <PreferenceItem>
                  <strong>Pets:</strong> {profileData.basicInfo.pets ? profileData.basicInfo.pets.charAt(0).toUpperCase() + profileData.basicInfo.pets.slice(1).replace(/-/g, ' ') : 'Not specified'}
                </PreferenceItem>
                <PreferenceItem>
                  <strong>Kids:</strong> {profileData.basicInfo.kids ? profileData.basicInfo.kids.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Not specified'}
                </PreferenceItem>
              </ProfilePreferences>

              {/* Zodiac (Optional) */}
              <ProfilePreferences>
                <h4>Fun Facts</h4>
                <PreferenceItem>
                  <strong>Zodiac Sign:</strong> {profileData.basicInfo.zodiac || 'Not specified'}
                </PreferenceItem>
              </ProfilePreferences>

              {/* Preferences (only show if not own profile) */}
              {!isOwnProfile && (
                <ProfilePreferences>
                  <h4>Looking For</h4>
                  <PreferenceItem>
                    <strong>Age:</strong> {profileData.preferences.ageRange.min}-{profileData.preferences.ageRange.max} years
                  </PreferenceItem>
                  <PreferenceItem>
                    <strong>Distance:</strong> Within {profileData.preferences.distance} miles
                  </PreferenceItem>
                  <PreferenceItem>
                    <strong>Gender:</strong> {profileData.preferences.genderPreference.join(', ')}
                  </PreferenceItem>
                  <PreferenceItem>
                    <strong>Relationship:</strong> {profileData.preferences.interestedIn.join(', ')}
                  </PreferenceItem>
                </ProfilePreferences>
              )}
            </ProfileTabContent>

            {/* Interests Tab */}
            <ProfileTabContent $isVisible={activeTab === 'interests'}>
              {profileData.interests.length > 0 && (
                <ProfileInterests>
                  <h4>Interests</h4>
                  <div className="interests-grid">
                    {profileData.interests.map(interest => (
                      <InterestTag key={interest.id}>
                        <span className="icon">{interest.icon}</span>
                        <span className="name">{interest.name}</span>
                      </InterestTag>
                    ))}
                  </div>
                </ProfileInterests>
              )}
            </ProfileTabContent>
          </ProfileCard>

          {/* Last thing on someone else's profile. Reporting belongs where you
              encounter a person, not on a settings page you have to go
              looking for. */}
          {!isOwnProfile && showSafetyActions && targetUserId && (
            <ProfileSafetyActions
              profileId={targetUserId}
              firstName={profileData.basicInfo.firstName}
            />
          )}

        </ProfileViewContent>
      </ProfileViewContainer>

      {/* Story Upload Modal */}
      {isOwnProfile && (
        <StoryUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onUpload={handleStoryUpload}
          userId={user?.id || 'current-user'}
        />
      )}

      {/* Story Viewer */}
      <StoryViewer
        isOpen={isStoryViewerOpen}
        stories={activeStories}
        initialIndex={0}
        onClose={() => setIsStoryViewerOpen(false)}
        userId={user?.id}
        userName={`${profileData.basicInfo.firstName} ${profileData.basicInfo.lastName}`}
      />

      {/* Full Screen Photo Viewer */}
      <AnimatePresence>
        {isPhotoViewerOpen && (
          <PhotoViewerOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsPhotoViewerOpen(false)}
          >
            <PhotoViewerCloseButton onClick={() => setIsPhotoViewerOpen(false)}>
              ×
            </PhotoViewerCloseButton>

            <PhotoViewerContent onClick={(e) => e.stopPropagation()}>
              {profileData.photos[viewerIndex]?.type === 'video' ? (
                <motion.video
                  key={viewerIndex}
                  src={profileData.photos[viewerIndex]?.url}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  controls
                  autoPlay
                  playsInline
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <PhotoViewerImage
                  as={motion.img}
                  key={viewerIndex}
                  src={profileData.photos[viewerIndex]?.url}
                  alt={`Photo ${viewerIndex + 1}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.2}
                  onDragEnd={(e, { offset, velocity }) => {
                    const swipe = offset.x;

                    if (swipe < -100) {
                      handleNextPhoto();
                    } else if (swipe > 100) {
                      handlePrevPhoto();
                    }
                  }}
                />
              )}

              {profileData.photos.length > 1 && (
                <>
                  <PhotoViewerNavButton
                    $position="left"
                    onClick={handlePrevPhoto}
                  >
                    ←
                  </PhotoViewerNavButton>
                  <PhotoViewerNavButton
                    $position="right"
                    onClick={handleNextPhoto}
                  >
                    →
                  </PhotoViewerNavButton>
                </>
              )}

              <PhotoViewerIndicator>
                {viewerIndex + 1} / {profileData.photos.length}
              </PhotoViewerIndicator>
            </PhotoViewerContent>
          </PhotoViewerOverlay>
        )}
      </AnimatePresence>
    </>
  );
};
